import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { apiGetUserProfile, apiPutUserProfile } from "../../utils/api";
import styles from "./ProfileModal.module.css";
import EmojiPicker from "emoji-picker-react";

const ProfileModal = ({ onClose }) => {
  const { token, login } = useAuth();

  const [formData, setFormData] = useState({
    name: "",
    tagname: "",
    password: "",
    description: "",
    avatar_emoji: "",
    politinterest: [],
    geninterest: [],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);

  const [newPolitTag, setNewPolitTag] = useState("");
  const [newGenTag, setNewGenTag] = useState("");

  const GEN_INTEREST_TAGS = [
    "Hund", "Katze", "Sport", "Reisen", "Musik", "Filme",
    "Kochen", "Lesen", "Technik", "Fotografie"
  ];

  const POLIT_INTEREST_TAGS = [
    "Schwurbel", "Deutschland", "Österreich", "Europa", "Klima",
    "Digitales", "Bildung", "Gesundheit", "Wirtschaft", "Migration"
  ];

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await apiGetUserProfile(token);
        const u = data.user;
        setFormData({
          name: u.name || "",
          tagname: u.tagname || "",
          password: "",
          description: u.description || "",
          avatar_emoji: u.avatar_emoji || "👤",
          politinterest: Array.isArray(u.politinterest) ? u.politinterest : [],
          geninterest: Array.isArray(u.geninterest) ? u.geninterest : [],
        });
      } catch (err) {
        setErrors({ general: err.error || "Fehler beim Laden" });
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const onEmojiClick = (emojiObject) => {
    setFormData((prev) => ({ ...prev, avatar_emoji: emojiObject.emoji }));
    setShowEmojiPicker(false);
  };

  const addPolitTag = (tag) => {
    if (!tag.trim()) return;
    if (!formData.politinterest.includes(tag.trim())) {
      setFormData(prev => ({
        ...prev,
        politinterest: [...prev.politinterest, tag.trim()]
      }));
    }
    setNewPolitTag("");
  };

  const removePolitTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      politinterest: prev.politinterest.filter(t => t !== tagToRemove)
    }));
  };

  const addGenTag = (tag) => {
    if (!tag.trim()) return;
    if (!formData.geninterest.includes(tag.trim())) {
      setFormData(prev => ({
        ...prev,
        geninterest: [...prev.geninterest, tag.trim()]
      }));
    }
    setNewGenTag("");
  };

  const removeGenTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      geninterest: prev.geninterest.filter(t => t !== tagToRemove)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name ist erforderlich";
    if (!formData.tagname.trim()) newErrors.tagname = "Tagname ist erforderlich";
    if (formData.password && formData.password.length < 6) {
      newErrors.password = "Passwort muss mindestens 6 Zeichen haben";
    }
    if (formData.avatar_emoji && formData.avatar_emoji.length > 10) {
      newErrors.avatar_emoji = "Maximal 10 Zeichen";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    setSuccessMsg("");
    try {
      const payload = { ...formData };
      if (!payload.password) delete payload.password;
      const data = await apiPutUserProfile(token, payload);
      login(data.user, token);
      setSuccessMsg("Profil erfolgreich aktualisiert!");
      setTimeout(() => onClose(), 100);
    } catch (err) {
      if (err.errors) setErrors(err.errors);
      else setErrors({ general: err.error || "Speichern fehlgeschlagen" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.overlay}>
        <div className={styles.modal}>Lade Profil...</div>
      </div>
    );
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3>Profil bearbeiten</h3>
        {errors.general && <div className={styles.error}>{errors.general}</div>}
        {successMsg && <div className={styles.success}>{successMsg}</div>}

        <form onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label>Name *</label>
            <input name="name" value={formData.name} onChange={handleChange} />
            {errors.name && <span className={styles.error}>{errors.name}</span>}
          </div>

          <div className={styles.field}>
            <label>Tagname * (einmalig, ändern möglich)</label>
            <input name="tagname" value={formData.tagname} onChange={handleChange} />
            {errors.tagname && <span className={styles.error}>{errors.tagname}</span>}
          </div>

          <div className={styles.field}>
            <label>Neues Passwort (leer lassen = unverändert)</label>
            <input type="password" name="password" value={formData.password} onChange={handleChange} />
            {errors.password && <span className={styles.error}>{errors.password}</span>}
          </div>

          <div className={styles.field}>
            <label>Beschreibung</label>
            <textarea name="description" value={formData.description} onChange={handleChange} rows="3" />
            {errors.description && <span className={styles.error}>{errors.description}</span>}
          </div>

          <div className={styles.field} style={{ position: "relative" }}>
            <label>Avatar Emoji (z.B. 😀)</label>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <input
                name="avatar_emoji"
                value={formData.avatar_emoji}
                onChange={handleChange}
                maxLength="10"
                style={{ flexGrow: 1 }}
              />
              <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                😊
              </button>
            </div>
            {showEmojiPicker && (
              <div ref={emojiPickerRef} className={styles.emojiPickerContainer}>
                <EmojiPicker onEmojiClick={onEmojiClick} />
              </div>
            )}
            {errors.avatar_emoji && <span className={styles.error}>{errors.avatar_emoji}</span>}
          </div>

          {/* Politisches Interesse als Tag-Liste */}
          <div className={styles.field}>
            <label>Politisches Interesse (Tags)</label>
            <div className={styles.tagInputWrapper}>
              <input
                type="text"
                value={newPolitTag}
                onChange={(e) => setNewPolitTag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPolitTag(newPolitTag))}
                placeholder="Tag eingeben und Enter drücken"
              />
              <button type="button" onClick={() => addPolitTag(newPolitTag)}>+</button>
            </div>
            <div className={styles.tagList}>
              {formData.politinterest.map(tag => (
                <span key={tag} className={styles.tag}>
                  {tag}
                  <button type="button" onClick={() => removePolitTag(tag)}>✖</button>
                </span>
              ))}
            </div>
            <SuggestionTags
              tags={POLIT_INTEREST_TAGS}
              onSelect={addPolitTag}
              label="Vorschläge:"
            />
          </div>

          {/* Allgemeines Interesse als Tag-Liste */}
          <div className={styles.field}>
            <label>Allgemeines Interesse (Tags)</label>
            <div className={styles.tagInputWrapper}>
              <input
                type="text"
                value={newGenTag}
                onChange={(e) => setNewGenTag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addGenTag(newGenTag))}
                placeholder="Tag eingeben und Enter drücken"
              />
              <button type="button" onClick={() => addGenTag(newGenTag)}>+</button>
            </div>
            <div className={styles.tagList}>
              {formData.geninterest.map(tag => (
                <span key={tag} className={styles.tag}>
                  {tag}
                  <button type="button" onClick={() => removeGenTag(tag)}>✖</button>
                </span>
              ))}
            </div>
            <SuggestionTags
              tags={GEN_INTEREST_TAGS}
              onSelect={addGenTag}
              label="Vorschläge:"
            />
          </div>

          <div className={styles.buttons}>
            <button type="submit" disabled={saving} className={styles.saveButtons}>
              {saving ? "Speichert..." : "Speichern"}
            </button>
            <button type="button" onClick={onClose} className={styles.cancelButtons}>
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Hilfskomponente für die Vorschlags-Chips
const SuggestionTags = ({ tags, onSelect, label }) => {
  if (!tags || tags.length === 0) return null;
  return (
    <div className={styles.suggestionContainer}>
      {label && <span className={styles.suggestionLabel}>{label}</span>}
      <div className={styles.suggestionList}>
        {tags.map(tag => (
          <button key={tag} type="button" className={styles.suggestionTag} onClick={() => onSelect(tag)}>
            {tag}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ProfileModal;